import { useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import { TOOLTIPS, type TooltipId } from "../lib/tooltips";

/** Space kept between the "?" and its popup, and between the popup and the window's edge. */
const GAP = 6;
const EDGE = 8;

/**
 * A small "?" beside a term that shows its line from the tooltip register
 * on hover, focus or tap. The text is also the control's accessible
 * description, so a screen reader hears it without the pointer.
 *
 * The popup is fixed to the window, not placed inside the page (#401): an
 * absolutely placed popup lay over the control the "?" labels and, inside
 * a dialog, over its buttons, and made the dialog scroll to fit it. It
 * opens above the "?" - what a label describes sits below or beside it -
 * and below only where the window has no room above.
 */
export function Hint({ id, label }: { id: TooltipId; label?: string }) {
  const text = TOOLTIPS[id];
  const descId = useId();
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLSpanElement>(null);

  const place = useCallback(() => {
    const b = button.current?.getBoundingClientRect();
    const p = popup.current?.getBoundingClientRect();
    if (!b || !p) return;
    const above = b.top - GAP - p.height;
    const top = above >= EDGE ? above : b.bottom + GAP;
    const left = Math.min(Math.max(b.left + b.width / 2 - p.width / 2, EDGE), window.innerWidth - p.width - EDGE);
    setAt({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setAt(null);
      return;
    }
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  return (
    <span className="relative inline-block align-middle">
      <button
        ref={button}
        type="button"
        aria-label={label ? `About ${label}` : "More about this"}
        aria-describedby={descId}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
        className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-neutral-400 text-[10px] font-semibold leading-none text-neutral-500 hover:border-ink hover:text-ink dark:border-neutral-600 dark:text-neutral-400 dark:hover:text-white"
      >
        ?
      </button>
      <span
        ref={popup}
        id={descId}
        role="tooltip"
        // Coordinates are measured, so they cannot be classes; everything else is.
        style={open && at ? { top: at.top, left: at.left } : undefined}
        // Closed, it is only the "?"'s description: sr-only and nothing else, or its width still widens the page (#401).
        className={open ? `fixed z-50 ${at ? "" : "invisible left-0 top-0"} pointer-events-none w-64 max-w-[calc(100vw-1rem)] rounded-lg border border-neutral-200 bg-white p-2.5 text-left text-xs font-normal normal-case tracking-normal text-neutral-800 shadow-lg dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100` : "sr-only"}
      >
        {text}
      </span>
    </span>
  );
}
